import { useReducer } from 'react';
import { Toast } from '@/lib/Toast';
import {
  Availability,
  AvailabilityInterval,
  Weekdays,
} from '@/types/api/companies';

interface IAvailabilitiesState {
  editingWeekday: Weekdays | undefined;
  availabilities: Availability[];
}

const AVAILABILITIES_TYPE = {
  EDITING_WEEKDAY: 'editing_weekday',
  REMOVE: 'remove',
  ADD_INTERVAL: 'add_interval',
  CHANGE_HOURS: 'change_hours',
  CHANGE_IS_ALWAYS_OPEN: 'toggle_is_always_open',
} as const;

type AvailabilitiesPayload = {
  [AVAILABILITIES_TYPE.EDITING_WEEKDAY]: Weekdays | undefined;
  [AVAILABILITIES_TYPE.REMOVE]: {
    availabilityIntervalId: number;
  };
  [AVAILABILITIES_TYPE.ADD_INTERVAL]: AvailabilityInterval;
  [AVAILABILITIES_TYPE.CHANGE_HOURS]: AvailabilityInterval;
  [AVAILABILITIES_TYPE.CHANGE_IS_ALWAYS_OPEN]: boolean;
};

export function getDateFromAvailabilityInterval(interval: string) {
  return new Date(`1970-01-01T${interval}`);
}

function availabilitiesReducer(
  state: IAvailabilitiesState,
  action: ActionMap<AvailabilitiesPayload>
) {
  const { availabilities, editingWeekday } = JSON.parse(
    JSON.stringify(state)
  ) as IAvailabilitiesState;
  const availabilityInAnalysis = availabilities[editingWeekday as Weekdays];

  switch (action.type) {
    case AVAILABILITIES_TYPE.EDITING_WEEKDAY: {
      return {
        ...state,
        editingWeekday: action.payload,
      };
    }

    case AVAILABILITIES_TYPE.REMOVE: {
      availabilityInAnalysis.intervals =
        availabilityInAnalysis.intervals.filter(
          ({ id }) => id !== action.payload.availabilityIntervalId
        );

      return {
        ...state,
        availabilities,
      };
    }

    case AVAILABILITIES_TYPE.ADD_INTERVAL: {
      availabilityInAnalysis.intervals =
        availabilityInAnalysis.intervals.concat(action.payload);

      return {
        ...state,
        availabilities,
      };
    }

    case AVAILABILITIES_TYPE.CHANGE_HOURS: {
      if (
        getDateFromAvailabilityInterval(action.payload.start) >=
        getDateFromAvailabilityInterval(action.payload.end)
      ) {
        Toast('error', 'A hora de início não pode ser posterior a hora final');
        return {
          ...state,
        };
      }

      const intervalsUpdated = availabilityInAnalysis.intervals.map(interval =>
        interval.id === action.payload.id ? action.payload : interval
      );

      const hasInvalidInterval = intervalsUpdated.find(
        (interval, i, intervals) => {
          const nextInterval = intervals[i + 1];

          if (
            getDateFromAvailabilityInterval(interval.end) >=
            getDateFromAvailabilityInterval(nextInterval?.start)
          ) {
            return interval;
          }
        }
      );

      if (hasInvalidInterval) {
        Toast(
          'error',
          'O segundo horário de atendimento não pode ser anterior ao primeiro horário'
        );
        return {
          ...state,
        };
      }

      availabilityInAnalysis.intervals = intervalsUpdated;

      return {
        ...state,
        availabilities,
      };
    }

    case AVAILABILITIES_TYPE.CHANGE_IS_ALWAYS_OPEN: {
      availabilityInAnalysis.is_always_open = action.payload;

      return {
        ...state,
        availabilities,
      };
    }

    default: {
      return state;
    }
  }
}

export const useAvailabilities = (initialStates: IAvailabilitiesState) => {
  const [state, dispatch] = useReducer(availabilitiesReducer, initialStates);

  return {
    state,
    editWeekday: (payload: AvailabilitiesPayload['editing_weekday']) =>
      dispatch({
        type: AVAILABILITIES_TYPE.EDITING_WEEKDAY,
        payload,
      }),
    changeIsAlwaysOpen: (
      payload: AvailabilitiesPayload['toggle_is_always_open']
    ) =>
      dispatch({
        type: AVAILABILITIES_TYPE.CHANGE_IS_ALWAYS_OPEN,
        payload,
      }),
    addAvailabilityInterval: (payload: AvailabilitiesPayload['add_interval']) =>
      dispatch({
        type: AVAILABILITIES_TYPE.ADD_INTERVAL,
        payload,
      }),
    editAvailabilityInterval: (
      payload: AvailabilitiesPayload['change_hours']
    ) =>
      dispatch({
        type: AVAILABILITIES_TYPE.CHANGE_HOURS,
        payload,
      }),
    removeAvailabilityInterval: (payload: AvailabilitiesPayload['remove']) =>
      dispatch({
        type: AVAILABILITIES_TYPE.REMOVE,
        payload,
      }),
  };
};
